import { find, findAll, setVisible } from "./lib/dom.js"
import "./portal-connector.js"

if (document.body.classList.contains("students.show")) {
  setVisible([find("#pidShow")].filter(Boolean), false)
}

find(".fa-eye")?.addEventListener("click", () => {
  setVisible([find("#pidShow")].filter(Boolean), true)
  setVisible([find("#pidHide")].filter(Boolean), false)
})

find(".fa-eye-slash")?.addEventListener("click", () => {
  setVisible([find("#pidShow")].filter(Boolean), false)
  setVisible([find("#pidHide")].filter(Boolean), true)
})

findAll("[data-password-toggle]").forEach((button) => {
  button.addEventListener("click", () => {
    const input = document.getElementById(button.dataset.passwordToggle)
    if (!input) return

    const isVisible = input.type === "text"
    input.type = isVisible ? "password" : "text"
    button.textContent = isVisible ? "顯示" : "隱藏"
    button.setAttribute("aria-pressed", String(!isVisible))
  })
})

find("[data-import-feedback]")?.focus({ preventScroll: true })

findAll("[data-student-import-form]").forEach((form) => {
  const prepareButton = form.querySelector("[data-import-prepare-url]")
  const refreshButton = form.querySelector("[data-import-refresh]")
  const challenge = form.querySelector("[data-import-challenge]")
  const captchaImage = form.querySelector("[data-import-captcha-image]")
  const captchaInput = form.querySelector("[data-import-captcha-input]")
  const flowToken = form.querySelector("[data-import-flow-token]")
  const submitButton = form.querySelector("[data-import-submit]")
  const errorBox = form.querySelector("[data-import-error]")
  const connectorPanel = find("[data-portal-connector]")
  const connectorReady = () => !connectorPanel || connectorPanel.dataset.connected === "true"
  let preparing = false
  let captchaRevision = 0
  if (connectorPanel) prepareButton.disabled = !connectorReady()
  document.addEventListener("portal-connector:state", (event) => {
    prepareButton.disabled = !event.detail.connected || preparing
    if (!event.detail.connected) {
      captchaRevision += 1
      challenge.hidden = true
      flowToken.value = ""
      captchaInput.disabled = true
      submitButton.disabled = true
    }
  })

  const showError = (message) => {
    errorBox.textContent = message
    errorBox.hidden = false
  }

  const prepareCaptcha = async () => {
    if (preparing) return
    if (!connectorReady()) return showError("請先連接本機工具。")
    preparing = true
    const revision = ++captchaRevision
    errorBox.hidden = true
    challenge.hidden = true
    captchaInput.disabled = true
    submitButton.disabled = true
    prepareButton.disabled = true
    prepareButton.textContent = "正在取得…"

    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
      const body = new FormData()
      body.append("stage", form.dataset.studentImportForm)
      const response = await fetch(prepareButton.dataset.importPrepareUrl, {
        method: "POST",
        headers: csrfToken ? { "X-CSRF-Token": csrfToken, Accept: "application/json" } : { Accept: "application/json" },
        body,
        credentials: "same-origin"
      })
      const payload = await response.json()
      if (!response.ok) throw new Error(payload.error || "無法取得圖片驗證碼。")
      if (revision !== captchaRevision || !connectorReady()) throw new Error("工具連線已變更，請重新取得驗證碼。")

      captchaImage.src = payload.captcha_data_url
      flowToken.value = payload.flow_token
      captchaInput.value = ""
      captchaInput.disabled = false
      challenge.hidden = false
      submitButton.disabled = false
      captchaInput.focus()
    } catch (error) {
      showError(error.message || "無法取得圖片驗證碼，請稍後再試。")
    } finally {
      preparing = false
      prepareButton.disabled = !connectorReady()
      prepareButton.textContent = challenge.hidden ? "重新取得圖片驗證碼" : "取得新驗證碼"
    }
  }

  prepareButton?.addEventListener("click", prepareCaptcha)
  refreshButton?.addEventListener("click", prepareCaptcha)
  form.addEventListener("submit", () => {
    submitButton.disabled = true
    submitButton.value = "正在登入…"
  })
})

// 背景匯入的進度輪詢。整批匯入動輒數分鐘，沒有進度回饋使用者會以為系統當掉。
const progressPanel = find("[data-import-progress]")
if (progressPanel) {
  const title = progressPanel.querySelector("[data-import-progress-title]")
  const eta = progressPanel.querySelector("[data-import-progress-eta]")
  const bar = progressPanel.querySelector("[data-import-progress-bar]")
  const fill = progressPanel.querySelector("[data-import-progress-fill]")
  const detail = progressPanel.querySelector("[data-import-progress-detail]")
  let timer = null

  const render = (data) => {
    if (data.state === "idle") {
      progressPanel.hidden = true
      return
    }
    progressPanel.hidden = false
    progressPanel.dataset.state = data.state

    if (data.state === "failed") {
      title.textContent = `${data.stage_name}匯入失敗`
      eta.textContent = ""
      detail.textContent = data.error || "請稍後再試。"
      setFill(0)
      return
    }

    if (data.state === "succeeded") {
      title.textContent = `${data.stage_name}匯入完成`
      eta.textContent = data.next_stage_name ? `接下來可以匯入${data.next_stage_name}學生` : ""
      detail.textContent = [data.summary, ...(data.warnings || [])].filter(Boolean).join("　")
      setFill(100)
      return
    }

    title.textContent = `正在匯入${data.stage_name}學生`
    if (data.total === null || data.total === undefined) {
      // 還沒拿到清冊，這時候顯示 0% 會誤導成「卡住了」
      eta.textContent = ""
      detail.textContent = "正在向通報網取得學生名單…"
      setFill(null)
      return
    }
    eta.textContent = data.estimated_remaining ? `預計還要 ${data.estimated_remaining}` : ""
    detail.textContent = `已處理 ${data.processed} / ${data.total} 位`
    setFill(data.percent ?? 0)
  }

  const setFill = (percent) => {
    if (percent === null) {
      progressPanel.classList.add("is-indeterminate")
      fill.style.width = "100%"
      bar.removeAttribute("aria-valuenow")
      return
    }
    progressPanel.classList.remove("is-indeterminate")
    fill.style.width = `${percent}%`
    bar.setAttribute("aria-valuenow", String(percent))
  }

  const poll = async () => {
    try {
      const response = await fetch(progressPanel.dataset.importStatusUrl, {
        headers: { Accept: "application/json" },
        credentials: "same-origin",
      })
      if (!response.ok) return
      const data = await response.json()
      render(data)
      if (data.finished || data.state === "idle") {
        clearInterval(timer)
        timer = null
      }
    } catch (error) {
      // 單次輪詢失敗不必打斷使用者，下一次會再試
    }
  }

  poll()
  timer = setInterval(poll, 3000)
}

find("#search_student")?.addEventListener("click", (event) => {
  const input = find("#pid")
  if (!input) return

  const pid = input.value.replace(/\s/g, "").toUpperCase()
  const message = checkId(pid)
  input.value = pid
  input.classList.toggle("is-valid", message === "ok")
  input.classList.toggle("is-invalid", message !== "ok")

  if (message !== "ok") {
    const feedback = find("#valid-message")
    if (feedback) feedback.value = message
    event.preventDefault()
    event.stopPropagation()
  }
})

// 依臺灣身分證字號規則檢查格式與檢查碼。
function checkId(id) {
  const letters = "ABCDEFGHJKLMNPQRSTUVXYWZIO"
  const weights = [1, 8, 7, 6, 5, 4, 3, 2, 1, 1]

  if (id.length !== 10) return "身份證字號共有10碼！"
  if (!/^[A-Z][12]\d{8}$/.test(id)) return "身份證號碼格式錯誤！"

  const letterValue = letters.indexOf(id.charAt(0)) + 10
  let sum = (Math.floor(letterValue / 10) + (letterValue % 10) * 9) % 10
  for (let index = 1; index < 10; index += 1) {
    sum += Number.parseInt(id.charAt(index), 10) * weights[index]
  }

  return sum % 10 === 0 ? "ok" : "這不是合理的身分證字號"
}
